<template>
  <button
    type="button"
    class="onboarding-radio-card"
    :class="{ 'onboarding-radio-card--selected': selected }"
    @click="$emit('select', value)"
  >
    <span class="onboarding-radio-card__control" aria-hidden="true">
      <span class="onboarding-radio-card__dot"></span>
    </span>

    <span class="onboarding-radio-card__content">
      <span v-if="title" class="onboarding-radio-card__title">{{ title }}</span>
      <span v-if="subtitle" class="onboarding-radio-card__subtitle">
        {{ subtitle }}
      </span>
      <ul v-if="items.length > 0" class="onboarding-radio-card__items">
        <li v-for="item in items" :key="item">{{ item }}</li>
      </ul>
      <span v-if="description" class="onboarding-radio-card__description">
        {{ description }}
      </span>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

export default defineComponent({
  name: "OnboardingRadioCard",
  props: {
    value: {
      type: [String, Number] as PropType<string | number>,
      required: true,
    },
    modelValue: {
      type: [String, Number] as PropType<string | number | null>,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    items: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  emits: {
    select: (_value: string | number) => true,
  },
  computed: {
    selected(): boolean {
      return this.modelValue === this.value;
    },
  },
});
</script>

<style scoped>
.onboarding-radio-card {
  width: 100%;
  min-height: 96px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  background: #ffffff;
  border: 1px solid rgb(var(--v-theme-grey90, 225, 227, 229));
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}

.onboarding-radio-card:hover,
.onboarding-radio-card--selected {
  border-color: rgb(var(--v-theme-secondary50, 82, 112, 113));
}

.onboarding-radio-card--selected {
  background: #e9fbfd;
  border-color: #b9ecef;
}

.onboarding-radio-card:focus-visible {
  outline: 2px solid rgb(var(--v-theme-secondary50, 82, 112, 113));
  outline-offset: 2px;
}

.onboarding-radio-card__control {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #ffd7d7;
  border-radius: 50%;
  background: #ffffff;
}

.onboarding-radio-card--selected .onboarding-radio-card__control {
  border: 2px solid rgb(var(--v-theme-secondary50, 82, 112, 113));
}

.onboarding-radio-card__dot {
  width: 8px;
  height: 8px;
  display: block;
  border-radius: 50%;
  background: transparent;
}

.onboarding-radio-card--selected .onboarding-radio-card__dot {
  background: rgb(var(--v-theme-secondary50, 82, 112, 113));
}

.onboarding-radio-card__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.onboarding-radio-card__title {
  color: rgb(var(--v-theme-secondary20, 19, 82, 83));
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
}

.onboarding-radio-card__subtitle {
  margin-top: 4px;
  color: rgb(var(--v-theme-secondary20, 19, 82, 83));
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
}

.onboarding-radio-card__description {
  margin-top: 8px;
  color: rgb(var(--v-theme-secondary50, 82, 112, 113));
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
}

.onboarding-radio-card__items {
  margin: 0;
  padding-left: 18px;
  color: rgb(var(--v-theme-secondary20, 19, 82, 83));
  font-size: 13px;
  font-weight: 500;
  line-height: 1.22;
}
</style>
